import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from '@hydrodigital/combobox';
import type { ComboboxOption } from '@hydrodigital/combobox';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import useInfrastructure from '../../hooks/useInfrastructure';

const SelectInfrastructure = forwardRef(
  (
    {
      location = null,
      organizationId = null,
      name = null,
      onSelect,
      labelInValue = false,
      emitObject = false,
      allowClear = true,
      defaultActiveFirstOption = true,
      mode,
    }: {
      location?: number | null;
      organizationId?: number | null;
      name?: string | null;
      onSelect: Function;
      labelInValue?: boolean;
      emitObject?: boolean;
      allowClear?: boolean;
      defaultActiveFirstOption?: boolean;
      mode: 'ByLocation' | 'ByOrganization';
    },
    ref,
  ) => {
    const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined);
    const { infrastructures, setParams } = useInfrastructure({
      name,
      mode,
      location,
      organizationId,
      size: 50,
    });

    useImperativeHandle(ref, () => ({
      setValue: (value: any) => {
        setSelectedValue(value !== null && value !== undefined ? String(value) : undefined);
      },
      cleanValues: () => {
        setSelectedValue(undefined);
        setParams((prev: any) => {
          if (prev.name !== null) {
            return { ...prev, name: null };
          }
          return prev;
        });
      },
    }));

    useEffect(() => {
      setSelectedValue(undefined);
      setParams((prev: any) => {
        if (prev.location !== location) {
          return { ...prev, location };
        }
        return prev;
      });
    }, [location, setParams]);

    useEffect(() => {
      if (
        infrastructures?.length > 0 &&
        !selectedValue &&
        defaultActiveFirstOption
      ) {
        const defaultOption = infrastructures[0];
        const val = emitObject ? JSON.stringify(defaultOption) : String(defaultOption.id);
        setSelectedValue(val);
        onSelect(emitObject ? defaultOption : defaultOption.id);
      }
    }, [
      infrastructures,
      emitObject,
      selectedValue,
      onSelect,
      defaultActiveFirstOption,
    ]);

    const items: ComboboxOption[] = (infrastructures || []).map((i) => ({
      value: emitObject ? JSON.stringify(i) : String(i.id),
      label: i.name,
    }));

    return (
      <Combobox
        items={items}
        value={selectedValue}
        onValueChange={(val) => {
          setSelectedValue(val);
          if (emitObject) {
            try {
              const parsed = JSON.parse(val);
              onSelect(parsed);
            } catch (e) {
              console.error('Error parsing infrastructure value:', e);
            }
          } else {
            onSelect(val);
          }
        }}
        onSearchChange={(newValue: string) => {
          setParams((prev: any) => {
            if (prev.name !== newValue) {
              return { ...prev, name: newValue };
            }
            return prev;
          });
        }}
        onClear={() => {
          setSelectedValue(undefined);
          onSelect(null);
          setParams((prev: any) => {
            if (prev.name !== null) {
              return { ...prev, name: null };
            }
            return prev;
          });
        }}
        searchable
        size="small"
      >
        <ComboboxInput placeholder="Select Asset" />
        <ComboboxContent>
          <ComboboxEmpty>No assets found.</ComboboxEmpty>
          <ComboboxList>
            {(item) => (
              <ComboboxItem key={item.value} value={item}>
                {item.label}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  },
);

export default SelectInfrastructure;
